import { Component, ViewChild, ChangeDetectorRef, input, effect, output, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { AppSettings } from "../../app.settings";
import { AuctionType } from "../../models/auctionType";
import { PropertyFeatures } from "../../models/propertyFeatures";
import { PropertyFilterParameters, PropertySearch } from "../../models/propertyFilterParameters";
import { PropertyType } from "../../models/propertyType";
import { SaleType } from "../../models/saleType";
import { State } from "../../models/state";
import { PropertyService } from "../../services/PropertyService";

@Component({
  standalone: false,
  selector: 'app-property-filter',
  templateUrl: './property-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./property-filter.component.scss']
})
export class PropertyFilterComponent {
  @ViewChild('auctionTypeFilter') auctionTypeFilter: any;
  @ViewChild('propertyTypeFilter') propertyTypeFilter: any;
  @ViewChild('stateFilter') stateFilter: any;
  @ViewChild('saleTypeFilter') saleTypeFilter: any;
  @ViewChild('propertyFeaturesFilter') propertyFeaturesFilter: any;

  readonly propertyFilterParameters = input<PropertyFilterParameters | undefined>(undefined);
  readonly routerPath = input<string | undefined>(undefined);

  readonly onPropertyFilterChange = output<any>();
  readonly onChangeViewType = output<any>();
  readonly onAllFilters = output<any>();

  private auctionTypesSelection?: AuctionType[];
  private propertyTypesSelection?: PropertyType[];
  private statesSelection?: State[];
  private saleTypesSelection?: SaleType[];
  private propertyFeaturesSelection?: PropertyFeatures;
  private currentPropertyFilterParameters?: PropertyFilterParameters;

  public filterForm: FormGroup;

  constructor(private appSettings: AppSettings, private cdRef: ChangeDetectorRef, private propertyService: PropertyService) {
    effect(() => {
      this.currentPropertyFilterParameters = this.propertyFilterParameters();
    });
    this.filterForm = new FormGroup({
      filter: new FormGroup({
        filterValue: new FormControl<string | undefined>(undefined)
      })
    });
  }

  ngAfterViewInit() {
    this.setPropertyFilterParameters();
  }

  public presetPropertyFilterParameters(parameters: PropertyFilterParameters) {
    this.currentPropertyFilterParameters = parameters;
    this.setPropertyFilterParameters();
  }

  private setPropertyFilterParameters() {
    if (this.currentPropertyFilterParameters) {
      this.auctionTypeFilter?.presetSelectedAuctionTypes(this.currentPropertyFilterParameters.auctionTypes);
      this.propertyTypeFilter?.presetSelectedPropertyTypes(this.currentPropertyFilterParameters.propertyTypes);
      this.propertyTypesSelection = this.currentPropertyFilterParameters.propertyTypes;
      this.stateFilter?.presetSelectedStates(this.currentPropertyFilterParameters.states);
      this.statesSelection = this.currentPropertyFilterParameters.states;
      this.saleTypeFilter?.presetSelectedSaleTypes(this.currentPropertyFilterParameters.saleTypes);
      this.saleTypesSelection = this.currentPropertyFilterParameters.saleTypes;
      this.propertyFeaturesFilter?.presetSelectedPropertyFeatures(this.currentPropertyFilterParameters.propertyFeatures);
      this.propertyFeaturesSelection = this.currentPropertyFilterParameters.propertyFeatures;
      this.filterForm.controls.filter.get('filterValue')!.setValue(this.currentPropertyFilterParameters.propertySearch?.searchString);
      this.cdRef.detectChanges();
    }
  }

  get showAuctionTypeFilter() {
    return this.routerPath() == "/available";
  }

  get anyFilterSet() {
    return this.auctionTypesSelection?.length || this.propertyTypesSelection?.length || this.statesSelection?.length || this.saleTypesSelection?.length || this.propertyFeaturesSelection?.definedFeatures  || this.filterForm.controls.filter.get('filterValue')!.value;
  }

  get filterLength() {
    return (this.auctionTypesSelection ? this.auctionTypesSelection.length : 0) + (this.propertyTypesSelection ? this.propertyTypesSelection.length : 0) + (this.statesSelection ? this.statesSelection.length : 0) + (this.saleTypesSelection ? this.saleTypesSelection.length : 0) + (this.propertyFeaturesSelection ? this.propertyFeaturesSelection.definedFeatures : 0);
  }

  public onClearFiltersClick() {
    this.auctionTypeFilter?.onClearClick();
    this.propertyTypeFilter?.onClearClick();
    this.stateFilter?.onClearClick();
    this.saleTypeFilter?.onClearClick();
    this.propertyFeaturesFilter?.onClearClick();
    this.clearSearchFilter();
  }

  public onAllFiltersClick() {
    this.onAllFilters.emit(undefined);
  }

  public auctionTypeSelectionChanged(auctionTypes: AuctionType[] | undefined) {
    this.auctionTypesSelection = auctionTypes;
    this.updatePropertyFilter();
  }

  public propertyTypeSelectionChanged(propertyTypes: PropertyType[] | undefined) {
    this.propertyTypesSelection = propertyTypes;
    this.updatePropertyFilter();
  }

  public stateSelectionChanged(states: State[] | undefined) {
    this.statesSelection = states;
    this.updatePropertyFilter();
  }

  public saleTypeSelectionChanged(saleTypes: SaleType[] | undefined) {
    this.saleTypesSelection = saleTypes;
    this.updatePropertyFilter();
  }

  public propertyFeaturesSelectionChanged(propertyFeatures: PropertyFeatures | undefined) {
    this.propertyFeaturesSelection = propertyFeatures;
    this.updatePropertyFilter();
  }

  public applySearchFilter() {
    this.updatePropertyFilter();
  }

  private clearSearchFilter() {
    this.filterForm.controls.filter.get('filterValue')!.setValue('');
    this.updatePropertyFilter();
  }

  private updatePropertyFilter() {
    let params = new PropertyFilterParameters();
    params.auctionTypes = this.auctionTypesSelection;
    params.propertyTypes = this.propertyTypesSelection;
    params.states = this.statesSelection;
    params.saleTypes = this.saleTypesSelection;
    params.propertyFeatures = this.propertyFeaturesSelection;
    let searchValue = this.filterForm.controls.filter.get('filterValue')!.value;
    params.propertySearch = searchValue ? new PropertySearch(searchValue?.toLowerCase(), undefined) : undefined;
    this.onPropertyFilterChange.emit(params);
  }

  public toggleViewType() {
    if (this.viewType == 'map' || this.viewType == 'mapOnly') this.onChangeViewType.emit('grid');
    else this.onChangeViewType.emit('map');
  }

  public get viewType() {
    return this.appSettings.settings.propertiesViewType;
  }
}